import _ from 'lodash';
import { Environment } from '../Environment';

type MNOHSFetchCommonArgs = {
  method: 'GET' | 'POST' | 'PATCH';
  url: string;
  query?: { [name: string]: any };
  accessToken: string;
  disableAuth?: boolean;
  /** show this error instead of BE's */
  showThisError?: boolean;
  /** A message to append as prefix to error message, also serves as fallback error message */
  errPrefix?: string;
  /** A message to append as suffix to error message */
  errSuffix?: string;
  /** An optional abort controller for fetch API */
  abortController?: AbortController;
};

interface MNOHSGetFetchArgs extends MNOHSFetchCommonArgs {
  type?: undefined;
}

interface MNOHSJsonFetchArgs extends MNOHSFetchCommonArgs {
  type: 'JSON';
  body: { [name: string]: any };
}

interface MNOHSFormFetchArgs extends MNOHSFetchCommonArgs {
  type: 'FORM_DATA';
  body: FormData;
}

/**
 * A wrapper for fetch that handles inclusion of accessToken
 * and formats caught error into readable form [and more]
 * dramatically decreases boilerplate code for fetch
 *
 * This does not catch errors originating from `fetch`
 * You have to catch the errors outside
 */
export const mnohsFetch = async (
  baseURL: string,
  args: MNOHSGetFetchArgs | MNOHSJsonFetchArgs | MNOHSFormFetchArgs
) => {
  const { services: sv } = Environment.current();

  // id=hi&item=1
  const qs = args.query
    ? (Object.keys(args.query) as Array<keyof typeof args.query>).map(key => `${key}=${args.query?.[key]}`).join('&')
    : '';

  let body = undefined;
  let headers = undefined;
  switch (args.type) {
    case 'JSON':
      headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      body = !_.isEmpty(args.body) ? JSON.stringify(args.body) : undefined;
      break;
    case 'FORM_DATA':
      // backend validation fails if I add this header
      // headers = { 'Content-Type': 'multipart/form-data' };
      body = args.body;
      break;
  }

  if (!args.disableAuth) {
    headers = { ...headers, Authorization: `Bearer ${args.accessToken}` };
  }

  // log request
  sv.logger.log(
    [
      ``,
      `[!] MAKING REQUEST - ${args.method} ${baseURL}${args.url}`,
      `[!] Headers: ${JSON.stringify(headers, undefined, 4)}`,
      `[!] Query: ${qs}`,
      args.type && `[!] Body: ${JSON.stringify(args.body, undefined, 4)}`,
    ].join('\n')
  );

  const fullURI = `${baseURL}${args.url}${qs ? `?${qs}` : ''}`;
  const response = await fetch(fullURI, {
    method: args.method,
    headers,
    body,
    signal: args.abortController?.signal,
  });

  if (response.ok) {
    // log response
    sv.logger.log([``, `[!] RECEIVED RESPONSE (${response.status}) - ${args.method} ${baseURL}${args.url}`].join('\n'));

    return response;
  } else {
    const { errPrefix = '', errSuffix = '', showThisError } = args;
    const defaultErr = `${errPrefix} (ERR: ${response.status}) ${errSuffix}`;

    const _throwRecognizedError = (append?: string) => {
      if (response.status === 401) {
        throw new Error('You are unauthorized to make this request!');
      } else if (response.status === 403) {
        throw new Error('You are forbidden to make this request!');
      } else if (response.status === 500) {
        throw new Error('Server failed to process your request!');
      } else if (response.status === 404) {
        throw new Error(`${args.errPrefix || 'Resource'} not found`);
      } else {
        throw new Error(`${defaultErr}${append ? ' - ' + append : ''}`);
      }
    };

    let serverError = '';

    try {
      const errorAsText = await response.text();
      if (errorAsText) {
        // see first if the server returned any error messages
        try {
          // if the server returns in format of { error, innerError }
          const error = JSON.parse(errorAsText);
          serverError = error.innerError;
        } catch (e) {
          // if the server just returns plain text
          serverError = errorAsText;
        }
      } else {
        // if not, just throw your defined errors above
        _throwRecognizedError();
      }
    } catch (e) {
      // if response.text() fails, append the error after the default error
      _throwRecognizedError(e.message);
    }

    // this won't get executed if the Errors above gets thrown first
    throw new Error(showThisError ? defaultErr : serverError);
  }
};
